import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ListObject } from 'app/main/design-system/models/listObjectsModel';
import {
  ConversionRateDTO,
  FranchiseFeatureType,
  FranchiseInDTO,
  FranchisePartnerDTO,
  SenderIdErrorType,
} from '../../franchises/type';
import { LoyaltyModelWithoutOffersDTO } from '../../loyalties/type';
import { fetchMinimalStoreListThunk } from '../../stores/ducks/stores.thunk';
import { StoreItemDTO } from '../../stores/type';
import { OwnerDTO, UberallUserDTO } from '../types/type';
import {
  adminEditStoreSettingsThunk,
  adminSaveStoreCtNumThunk,
  fetchFranchisePartner,
  getOwnersThunk,
  updateFranchiseFeatureStatus,
  updateFranchisePartner,
  updateFranchiseStatus,
} from './admin.thunk';

export interface AdminState {
  franchise: {
    entity?: FranchiseInDTO;
    isLoading: boolean;
    areFeaturesLoading: boolean;
    partner?: FranchisePartnerDTO;
    isPartnerSaving: boolean;
    hasError: boolean;

    senderIdValidity: {
      isLoading: boolean;
      isValid: boolean;
      errorType: SenderIdErrorType | null;
    };

    senderIdModal: {
      isOpened: boolean;
    };
    deactivationDialog: {
      isOpen: boolean;
      isLoading: boolean;
      hasError: boolean;
    };
  };

  stores: {
    entities: StoreItemDTO[];
    areLoading: boolean;
    hasError: boolean;
  };

  loyaltyModel: {
    entity?: LoyaltyModelWithoutOffersDTO;
    isLoading: boolean;
    hasError: boolean;
  };

  conversionRate: {
    entity?: ConversionRateDTO;
    isLoading: boolean;
    hasError: boolean;
  };
  owners: OwnerDTO[];
  uberallUsers: UberallUserDTO[];
}

const initialState: AdminState = {
  franchise: {
    entity: undefined,
    isLoading: false,
    hasError: false,
    areFeaturesLoading: false,
    partner: undefined,
    isPartnerSaving: false,

    senderIdValidity: {
      isLoading: false,
      isValid: true,
      errorType: null,
    },

    senderIdModal: {
      isOpened: false,
    },
    deactivationDialog: {
      isOpen: false,
      isLoading: false,
      hasError: false,
    },
  },

  stores: {
    entities: [],
    areLoading: false,
    hasError: false,
  },

  loyaltyModel: {
    entity: undefined,
    isLoading: false,
    hasError: false,
  },

  conversionRate: {
    entity: undefined,
    isLoading: false,
    hasError: false,
  },
  owners: [],
  uberallUsers: [],
};

// A response for a franchise the admin already left must not replace the marker of the displayed one.
const storePartnerOfDisplayedFranchise = (state: AdminState, partner: FranchisePartnerDTO) => {
  if (partner.id === state.franchise.entity?.id) {
    state.franchise.partner = partner;
  }
};

export const { actions, reducer } = createSlice({
  name: 'admin',
  initialState,
  reducers: {
    setIsAdminFranchiseLoading: (state, action: PayloadAction<boolean>) => {
      state.franchise.isLoading = action.payload;
    },
    getAdminFranchiseSuccess: (state, action: PayloadAction<FranchiseInDTO>) => {
      state.franchise.entity = action.payload;
      state.franchise.hasError = false;
    },
    getAdminFranchiseFailed: state => {
      state.franchise.entity = undefined;
      state.franchise.hasError = true;
    },
    updateAdminFranchise: (state, action: PayloadAction<FranchiseInDTO>) => {
      state.franchise.entity = action.payload;
    },
    setAdminFranchiseSenderIdLoading: (state, action: PayloadAction<boolean>) => {
      state.franchise.senderIdValidity.isLoading = action.payload;
    },
    setAdminFranchiseSenderIdValidity: (state, action: PayloadAction<boolean>) => {
      state.franchise.senderIdValidity.isValid = action.payload;
    },
    setAdminFranchiseSenderIdErrorType: (state, action: PayloadAction<SenderIdErrorType | null>) => {
      state.franchise.senderIdValidity.errorType = action.payload;
    },
    setAdminFranchiseSenderIdModalState: (state, action: PayloadAction<boolean>) => {
      state.franchise.senderIdModal.isOpened = action.payload;
    },
    setIsAdminLoyaltyModelLoading: (state, action: PayloadAction<boolean>) => {
      state.loyaltyModel.isLoading = action.payload;
    },
    getAdminLoyaltyModelSuccess: (state, action: PayloadAction<LoyaltyModelWithoutOffersDTO>) => {
      state.loyaltyModel.entity = action.payload;
      state.loyaltyModel.hasError = false;
    },
    getAdminLoyaltyModelFailed: state => {
      state.loyaltyModel.entity = undefined;
      state.loyaltyModel.hasError = true;
    },
    setIsAdminConversionRateLoading: (state, action: PayloadAction<boolean>) => {
      state.conversionRate.isLoading = action.payload;
    },
    getAdminConversionRateSuccess: (state, action: PayloadAction<ConversionRateDTO>) => {
      state.conversionRate.entity = action.payload;
      state.conversionRate.hasError = false;
    },
    getAdminConversionRateFailed: state => {
      state.conversionRate.entity = undefined;
      state.conversionRate.hasError = true;
    },
    setAdminFranchiseDeactivationDialog: (
      state,
      action: PayloadAction<{ isOpen?: boolean; isLoading?: boolean; hasError?: boolean }>,
    ) => {
      state.franchise.deactivationDialog = {
        ...state.franchise.deactivationDialog,
        ...action.payload,
      };
    },
    setAdminUberallUsers: (state, action: PayloadAction<UberallUserDTO[]>) => {
      state.uberallUsers = action.payload;
    },
    setStoreCtNum: (state, action: PayloadAction<{ storeId: string; ctNum?: string }>) => {
      const { storeId, ctNum } = action.payload;
      state.stores.entities = state.stores.entities.map(store =>
        store.id === storeId ? { ...store, ctNum: ctNum ?? store.ctNum } : store,
      );
    },
  },
  extraReducers: builder => {
    builder.addCase(fetchMinimalStoreListThunk.pending, state => {
      state.stores.areLoading = true;
    });
    builder.addCase(fetchMinimalStoreListThunk.fulfilled, (state, action: PayloadAction<StoreItemDTO[]>) => {
      state.stores.entities = action.payload;
      state.stores.areLoading = false;
      state.stores.hasError = false;
    });
    builder.addCase(fetchMinimalStoreListThunk.rejected, state => {
      state.stores.entities = [];
      state.stores.areLoading = false;
      state.stores.hasError = true;
    });
    builder.addCase(updateFranchiseFeatureStatus.pending, state => {
      state.franchise.areFeaturesLoading = true;
    });
    builder.addCase(updateFranchiseFeatureStatus.fulfilled, (state, action: PayloadAction<FranchiseFeatureType[]>) => {
      if (state.franchise.entity) {
        state.franchise.entity.features = action.payload;
      }
      state.franchise.areFeaturesLoading = false;
      state.franchise.hasError = false;
    });
    builder.addCase(updateFranchiseFeatureStatus.rejected, state => {
      state.franchise.areFeaturesLoading = false;
      state.franchise.hasError = true;
    });
    builder.addCase(updateFranchisePartner.pending, state => {
      state.franchise.isPartnerSaving = true;
    });
    builder.addCase(fetchFranchisePartner.pending, state => {
      state.franchise.partner = undefined;
    });
    builder.addCase(fetchFranchisePartner.fulfilled, (state, action: PayloadAction<FranchisePartnerDTO>) => {
      storePartnerOfDisplayedFranchise(state, action.payload);
    });
    builder.addCase(updateFranchisePartner.fulfilled, (state, action: PayloadAction<FranchisePartnerDTO>) => {
      storePartnerOfDisplayedFranchise(state, action.payload);
      state.franchise.isPartnerSaving = false;
    });
    builder.addCase(updateFranchisePartner.rejected, state => {
      state.franchise.isPartnerSaving = false;
    });
    builder.addCase(updateFranchiseStatus.pending, state => {
      state.franchise.deactivationDialog.isLoading = true;
    });
    builder.addCase(updateFranchiseStatus.fulfilled, (state, action: PayloadAction<FranchiseInDTO>) => {
      state.franchise.entity = action.payload;
      state.franchise.deactivationDialog.isLoading = false;
      state.franchise.deactivationDialog.hasError = false;
    });
    builder.addCase(updateFranchiseStatus.rejected, state => {
      state.franchise.deactivationDialog.isLoading = false;
      state.franchise.deactivationDialog.hasError = true;
    });
    builder.addCase(adminEditStoreSettingsThunk.pending, state => {
      state.stores.areLoading = true;
    });
    builder.addCase(adminEditStoreSettingsThunk.fulfilled, state => {
      state.stores.areLoading = false;
      state.stores.hasError = false;
    });
    builder.addCase(adminEditStoreSettingsThunk.rejected, state => {
      state.stores.hasError = true;
    });
    builder.addCase(getOwnersThunk.pending, state => {
      state.owners = [];
    });
    builder.addCase(getOwnersThunk.fulfilled, (state, action: PayloadAction<ListObject<OwnerDTO>>) => {
      state.owners = action.payload?.data ?? [];
    });
    builder.addCase(getOwnersThunk.rejected, state => {
      state.owners = [];
    });
    builder.addCase(adminSaveStoreCtNumThunk.pending, state => {
      state.stores.areLoading = true;
    });
    builder.addCase(adminSaveStoreCtNumThunk.fulfilled, state => {
      state.stores.areLoading = false;
      state.stores.hasError = false;
    });
    builder.addCase(adminSaveStoreCtNumThunk.rejected, state => {
      state.stores.hasError = true;
    });
  },
});

export const {
  setAdminFranchiseSenderIdErrorType,
  setAdminFranchiseSenderIdLoading,
  setAdminFranchiseSenderIdValidity,
  setAdminFranchiseSenderIdModalState,
  setAdminUberallUsers,
} = actions;

export default reducer;
